import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, deleteUser, formatCurrency } from "@/lib/api";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Users, 
  Building2, 
  Mail, 
  Phone, 
  Calendar,
  Shield,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Eye,
  Link as LinkIcon,
  ArrowRight,
  Trash2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  UserPlus,
  Repeat,
  CalendarClock,
  Percent,
  Sparkles,
  Activity,
  AlertTriangle
} from "lucide-react";
import { toast } from "sonner";
import { TeamMembersCard } from "@/components/TeamMembersCard";

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [partnerCount, setPartnerCount] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  const [usage, setUsage] = useState(null);
  const [usageMonth, setUsageMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [usageLoading, setUsageLoading] = useState(false);
  const [range, setRange] = useState(30);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userDetails, setUserDetails] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAdminAndFetch();
  }, []);

  const checkAdminAndFetch = async () => {
    try {
      // Check if user is admin
      const adminCheck = await api.get("/admin/check");
      if (!adminCheck.data.is_admin) {
        toast.error("Admin access required");
        navigate("/");
        return;
      }
      setIsAdmin(true);

      // Fetch users and partners
      const [usersRes, partnersRes, analyticsRes] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/partners"),
        api.get(`/admin/analytics?days=${range}`)
      ]);
      setUsers(usersRes.data.users);
      setTotalUsers(usersRes.data.total_users);
      setPartnerCount(partnersRes.data.total_partners);
      setAnalytics(analyticsRes.data);
      fetchUsage(usageMonth);
    } catch (error) {
      console.error("Error:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsage = async (month) => {
    setUsageLoading(true);
    try {
      const res = await api.get(`/admin/usage?month=${month}`);
      setUsage(res.data);
    } catch (error) {
      console.error("Error fetching usage:", error);
    } finally {
      setUsageLoading(false);
    }
  };

  const changeUsageMonth = (month) => {
    setUsageMonth(month);
    fetchUsage(month);
  };

  const changeRange = async (days) => {    setRange(days);
    setAnalyticsLoading(true);
    try {
      const res = await api.get(`/admin/analytics?days=${days}`);
      setAnalytics(res.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const toggleUserDetails = async (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
      return;
    }

    setExpandedUser(userId);

    if (!userDetails[userId]) {
      try {
        const response = await api.get(`/admin/users/${userId}`);
        setUserDetails(prev => ({
          ...prev,
          [userId]: response.data
        }));
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    }
  };

  const handleViewAsUser = async (userId, userName) => {
    try {
      const response = await api.post(`/admin/impersonate/${userId}`);
      toast.success(`Now viewing as ${userName}`);
      // Redirect to dashboard as that user
      navigate("/");
      // Force page reload to update auth state
      window.location.reload();
    } catch (error) {
      toast.error("Failed to view as user");
      console.error("Error impersonating user:", error);
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    
    setIsDeleting(true);
    try {
      await deleteUser(userToDelete.user_id);
      toast.success(`User ${userToDelete.email} deleted successfully`);
      // Remove user from list
      setUsers(users.filter(u => u.user_id !== userToDelete.user_id));
      setTotalUsers(prev => prev - 1);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
      console.error("Error deleting user:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-4 lg:p-8">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate("/")}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h2 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="w-8 h-8 text-[#547d91]" />
            Admin Dashboard
          </h2>
          <p className="text-slate-500 mt-1">
            Manage users and view account details
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#547d91]/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#547d91]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{totalUsers}</p>
                <p className="text-sm text-slate-500">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {users.reduce((acc, u) => acc + (u.property_count || 0), 0)}
                </p>
                <p className="text-sm text-slate-500">Total Properties</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Link to="/admin/partners">
          <Card className="cursor-pointer hover:bg-slate-50 transition-colors h-full">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <LinkIcon className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{partnerCount}</p>
                    <p className="text-sm text-slate-500">Referral Partners</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Admin Actions */}

      {/* Analytics */}
      {analytics && (
        <div className="space-y-4" data-testid="analytics-section">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#547d91]" />
              <h3 className="font-heading text-xl font-bold text-slate-900">Analytics</h3>
              {analytics.subscriptions && !analytics.subscriptions.stripe_available && (
                <Badge variant="outline" className="text-amber-600 border-amber-200">Stripe not connected</Badge>
              )}
            </div>
            {/* Date range filter */}
            <div className="inline-flex items-center rounded-lg bg-slate-100 p-1" data-testid="range-filter">
              {[
                { label: "7d", value: 7 },
                { label: "30d", value: 30 },
                { label: "90d", value: 90 },
                { label: "All time", value: 0 },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => changeRange(opt.value)}
                  disabled={analyticsLoading}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    range === opt.value ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid={`range-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Subscription KPIs */}
          <div className={`grid grid-cols-2 lg:grid-cols-4 gap-4 transition-opacity ${analyticsLoading ? "opacity-50" : ""}`}>
            <MetricCard
              testid="metric-mrr"
              icon={DollarSign}
              color="emerald"
              label="MRR (Monthly Recurring)"
              value={formatCurrency(analytics.subscriptions.mrr)}
              sub={`ARR ${formatCurrency(analytics.subscriptions.arr)}`}
            />
            <MetricCard
              testid="metric-subscribers"
              icon={Users}
              color="sky"
              label="Active Subscribers"
              value={analytics.subscriptions.total_subscribers}
              sub={`${analytics.subscriptions.active_paid} paid · ${analytics.subscriptions.trialing} trialing`}
            />
            <MetricCard
              testid="metric-churn"
              icon={TrendingDown}
              color="rose"
              label={`Churn Rate (${analytics.range_label})`}
              value={`${analytics.subscriptions.churn_rate}%`}
              sub={`${analytics.subscriptions.canceled_in_range} cancelled · ${analytics.subscriptions.canceling} pending`}
            />
            <MetricCard
              testid="metric-avg-period"
              icon={CalendarClock}
              color="violet"
              label="Avg. Subscription Length"
              value={`${Math.round(analytics.subscriptions.avg_subscription_days)} days`}
              sub={`~${(analytics.subscriptions.avg_subscription_days / 30).toFixed(1)} months`}
            />
          </div>

          {/* User + conversion KPIs */}
          <div className={`grid grid-cols-2 lg:grid-cols-4 gap-4 transition-opacity ${analyticsLoading ? "opacity-50" : ""}`}>
            <MetricCard
              testid="metric-new-users"
              icon={UserPlus}
              color="teal"
              label={`New Users (${analytics.range_label})`}
              value={analytics.users.new_in_range}
              sub={`${analytics.users.new_7d} in last 7 days`}
            />
            <MetricCard
              testid="metric-new-subs"
              icon={Sparkles}
              color="amber"
              label={`New Subscriptions (${analytics.range_label})`}
              value={analytics.subscriptions.new_subs_in_range}
              sub={`Started in ${analytics.range_label}`}
            />
            <MetricCard
              testid="metric-conversion"
              icon={Percent}
              color="indigo"
              label="Conversion Rate"
              value={`${analytics.subscriptions.conversion_rate}%`}
              sub="Subscribers ÷ total users"
            />
            <MetricCard
              testid="metric-referred"
              icon={LinkIcon}
              color="purple"
              label="Referred Users"
              value={analytics.users.referred}
              sub={`${analytics.users.direct} direct signups`}
            />
          </div>

          {/* Revenue trend + Plan mix */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="font-heading text-base">Revenue Trend — MRR (last 6 months)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64" data-testid="mrr-trend-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={analytics.subscriptions.mrr_trend} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                      <Tooltip formatter={(v) => [formatCurrency(v), "MRR"]} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                      <Line type="monotone" dataKey="mrr" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3, fill: "#10b981" }} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-base">Plan Mix</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4" data-testid="plan-mix">
                <PlanBar label="Monthly" value={analytics.subscriptions.monthly} total={analytics.subscriptions.total_subscribers} color="#547d91" />
                <PlanBar label="Annual" value={analytics.subscriptions.annual} total={analytics.subscriptions.total_subscribers} color="#10b981" />
                <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-sm">
                  <span className="text-slate-500 flex items-center gap-1"><Repeat className="w-4 h-4" /> On free trial</span>
                  <span className="font-semibold text-slate-900">{analytics.subscriptions.trialing}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500 flex items-center gap-1"><TrendingDown className="w-4 h-4" /> Cancelling</span>
                  <span className="font-semibold text-slate-900">{analytics.subscriptions.canceling}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Signups + Partner performance */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="font-heading text-base">New Signups (last 6 months)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64" data-testid="signups-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analytics.users.signups_by_month} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                      <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                      <Bar dataKey="signups" fill="#547d91" radius={[6, 6, 0, 0]} maxBarSize={56} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-base">Top Referral Partners</CardTitle>
              </CardHeader>
              <CardContent data-testid="partner-performance">
                {(!analytics.partner_performance || analytics.partner_performance.length === 0) ? (
                  <div className="text-center py-8">
                    <LinkIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No referred signups yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {analytics.partner_performance.map((p, idx) => (
                      <div key={idx} className="flex items-center justify-between" data-testid={`partner-perf-${idx}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-7 h-7 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-semibold shrink-0">
                            {idx + 1}
                          </div>
                          <span className="text-sm font-medium text-slate-800 truncate">{p.name}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm shrink-0">
                          <span className="text-slate-500" title="Signups">{p.signups} <span className="text-slate-400">signups</span></span>
                          <span className="font-semibold text-emerald-600" title="Paying subscribers">{p.paying} <span className="text-emerald-500/70 font-normal">paying</span></span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Usage & Spend */}
      <Card data-testid="usage-section">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="font-heading flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#547d91]" /> Usage &amp; Spend
            </CardTitle>
            <div className="flex items-center gap-3">
              {usage && (
                <div className="text-sm text-slate-500">
                  <span className="font-semibold text-slate-900">${usage.totals.spend.toLocaleString()}</span> across{" "}
                  <span className="font-semibold text-slate-900">{usage.totals.users}</span> user{usage.totals.users === 1 ? "" : "s"}
                  <span className="text-slate-400"> · cap ${usage.limits.spend_cap}/user</span>
                </div>
              )}
              <input
                type="month"
                value={usageMonth}
                onChange={(e) => changeUsageMonth(e.target.value)}
                className="border border-slate-200 rounded-md px-2 py-1 text-sm"
                data-testid="usage-month-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {usageLoading ? (
            <p className="text-sm text-slate-400 py-6 text-center">Loading usage…</p>
          ) : !usage || usage.users.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center" data-testid="usage-empty">No AI/data usage recorded for {usageMonth}.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-400 text-xs border-b border-slate-100">
                    <th className="px-3 py-2 text-left font-medium">User</th>
                    <th className="px-3 py-2 text-right font-medium">Spend</th>
                    <th className="px-3 py-2 text-right font-medium">Suburb</th>
                    <th className="px-3 py-2 text-right font-medium">Due Dil.</th>
                    <th className="px-3 py-2 text-right font-medium">Estimates</th>
                    <th className="px-3 py-2 text-right font-medium">AI Chat</th>
                    <th className="px-3 py-2 text-right font-medium">Lookups</th>
                    <th className="px-3 py-2 text-center font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.users.map((u) => {
                    const pctOfCap = Math.min(100, Math.round((u.total_spend / usage.limits.spend_cap) * 100));
                    const bar = u.capped ? "bg-red-500" : pctOfCap >= 75 ? "bg-amber-500" : "bg-emerald-500";
                    return (
                      <tr key={u.user_id} className="border-b border-slate-50" data-testid={`usage-row-${u.user_id}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-800">{u.name}</div>
                          <div className="text-xs text-slate-400">{u.email}</div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="font-semibold text-slate-900 tabular-nums">${u.total_spend.toFixed(2)}</div>
                          <div className="w-24 h-1.5 bg-slate-100 rounded-full ml-auto mt-1 overflow-hidden">
                            <div className={`h-full ${bar}`} style={{ width: `${pctOfCap}%` }} />
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-600">{u.counts.suburb_search}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-600">{u.counts.due_diligence}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-600">{u.counts.estimate}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-600">{u.counts.ai_chat}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-400">{u.counts.typeahead}</td>
                        <td className="px-3 py-2 text-center">
                          {u.capped ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 rounded-full px-2 py-0.5" title={u.cap_reason}>
                              <AlertTriangle className="w-3 h-3" /> Capped
                            </span>
                          ) : (
                            <span className="text-xs text-emerald-600">OK</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-xs text-slate-400 mt-3">
                Spend is an estimate based on per-action data costs. Monthly caps: {usage.limits.monthly.suburb_search} suburb · {usage.limits.monthly.due_diligence} due-diligence · {usage.limits.monthly.estimate} estimates · {usage.limits.monthly.ai_chat} chat · ${usage.limits.spend_cap} master. Change in backend env; admins aren't metered.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Internal Team Members */}
      <TeamMembersCard />

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Registered Users</CardTitle>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No users registered yet</p>
            </div>
          ) : (
            <>
            {/* Mobile card list */}
            <div className="md:hidden space-y-3" data-testid="users-mobile-list">
              {users.map((user) => (
                <div key={user.user_id} className="border border-slate-200 rounded-xl p-4" data-testid={`user-mobile-card-${user.user_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-[#547d91] flex items-center justify-center text-white text-sm font-medium shrink-0">
                        {user.name?.charAt(0) || "?"}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate">{user.name}</p>
                        <p className="text-sm text-slate-500 truncate">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => handleViewAsUser(user.user_id, user.name)} className="text-[#547d91]" data-testid={`view-as-mobile-${user.user_id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDeleteClick(user)} className="text-red-600" data-testid={`delete-user-mobile-${user.user_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3 flex-wrap text-sm">
                    {user.referral_partner_name ? (
                      <Badge variant="outline" className="text-purple-600 border-purple-200 text-xs">{user.referral_partner_name}</Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs text-slate-500">Direct</Badge>
                    )}
                    <span className="flex items-center gap-1 text-slate-500"><Building2 className="w-3.5 h-3.5" /> {user.property_count || 0}</span>
                    {user.mobile && <span className="flex items-center gap-1 text-slate-500"><Phone className="w-3.5 h-3.5" /> {user.mobile}</span>}
                    <span className="flex items-center gap-1 text-slate-400 ml-auto"><Calendar className="w-3.5 h-3.5" /> {formatDate(user.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="overflow-x-auto hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Referred By</TableHead>
                    <TableHead>Properties</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <>
                      <TableRow 
                        key={user.user_id} 
                        className="cursor-pointer hover:bg-slate-50"
                        data-testid={`user-row-${user.user_id}`}
                      >
                        <TableCell onClick={() => toggleUserDetails(user.user_id)}>
                          {expandedUser === user.user_id ? (
                            <ChevronUp className="w-4 h-4 text-slate-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-slate-400" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium" onClick={() => toggleUserDetails(user.user_id)}>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-[#547d91] flex items-center justify-center text-white text-sm font-medium">
                              {user.name?.charAt(0) || "?"}
                            </div>
                            {user.name}
                          </div>
                        </TableCell>
                        <TableCell onClick={() => toggleUserDetails(user.user_id)}>{user.email}</TableCell>
                        <TableCell onClick={() => toggleUserDetails(user.user_id)}>
                          {user.mobile ? (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3 text-slate-400" />
                              {user.mobile}
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell onClick={() => toggleUserDetails(user.user_id)}>
                          {user.referral_partner_name ? (
                            <Badge variant="outline" className="text-purple-600 border-purple-200">
                              {user.referral_partner_name}
                            </Badge>
                          ) : (
                            <span className="text-slate-400">Direct</span>
                          )}
                        </TableCell>
                        <TableCell onClick={() => toggleUserDetails(user.user_id)}>
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            {user.property_count || 0}
                          </span>
                        </TableCell>
                        <TableCell onClick={() => toggleUserDetails(user.user_id)}>
                          <span className="flex items-center gap-1 text-slate-500">
                            <Calendar className="w-3 h-3" />
                            {formatDate(user.created_at)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewAsUser(user.user_id, user.name);
                              }}
                              className="text-[#547d91] hover:text-[#456a7a]"
                              data-testid={`view-as-${user.user_id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(user);
                              }}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-user-${user.user_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {expandedUser === user.user_id && userDetails[user.user_id] && (
                        <TableRow>
                          <TableCell colSpan={8} className="bg-slate-50 p-4">
                            <div className="space-y-3">
                              <h4 className="font-semibold text-slate-700">User Properties</h4>
                              {userDetails[user.user_id].properties.length === 0 ? (
                                <p className="text-slate-500 text-sm">No properties added yet</p>
                              ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                  {userDetails[user.user_id].properties.map((prop) => (
                                    <div key={prop.id} className="bg-white p-3 rounded-lg border border-slate-200">
                                      <p className="font-medium text-slate-900">{prop.address}</p>
                                      <p className="text-sm text-slate-500">{prop.suburb}, {prop.state}</p>
                                      <div className="flex justify-between mt-2 text-sm">
                                        <span className="text-[#547d91] font-medium">
                                          ${prop.current_value?.toLocaleString()}
                                        </span>
                                        <span className="text-slate-500">
                                          ${prop.weekly_rent || prop.rental_income_weekly}/wk
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              <h4 className="font-semibold text-slate-700 pt-2">
                                Searched Properties{" "}
                                <span className="text-slate-400 font-normal">
                                  ({userDetails[user.user_id].research_reports?.length || 0})
                                </span>
                              </h4>
                              {(!userDetails[user.user_id].research_reports ||
                                userDetails[user.user_id].research_reports.length === 0) ? (
                                <p className="text-slate-500 text-sm">No property searches yet</p>
                              ) : (
                                <div className="space-y-2">
                                  {userDetails[user.user_id].research_reports.map((report) => (
                                    <div
                                      key={report.report_id}
                                      className="bg-white p-3 rounded-lg border border-slate-200 flex items-center justify-between gap-3"
                                    >
                                      <div className="min-w-0">
                                        <p className="font-medium text-slate-900 truncate">{report.address}</p>
                                        <p className="text-xs text-slate-500 capitalize">
                                          {report.provider} ·{" "}
                                          {report.created_at
                                            ? new Date(report.created_at).toLocaleString("en-AU", {
                                                day: "numeric",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              })
                                            : ""}
                                        </p>
                                      </div>
                                      {report.report_url && (
                                        <a
                                          href={report.report_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-sm text-[#547d91] hover:underline shrink-0"
                                        >
                                          View report
                                        </a>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
                </TableBody>
              </Table>
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{userToDelete?.name}</strong> ({userToDelete?.email})?
              <br /><br />
              This will permanently delete:
              <ul className="list-disc list-inside mt-2 text-red-600">
                <li>User account</li>
                <li>All {userToDelete?.property_count || 0} properties</li>
                <li>All associated expenses</li>
                <li>All session data</li>
              </ul>
              <br />
              <strong className="text-red-600">This action cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? "Deleting..." : "Delete User"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const COLOR_MAP = {
  emerald: "bg-emerald-100 text-emerald-600",
  sky: "bg-sky-100 text-sky-600",
  rose: "bg-rose-100 text-rose-600",
  violet: "bg-violet-100 text-violet-600",
  teal: "bg-teal-100 text-teal-600",
  amber: "bg-amber-100 text-amber-600",
  indigo: "bg-indigo-100 text-indigo-600",
  purple: "bg-purple-100 text-purple-600",
};

function MetricCard({ icon: Icon, color, label, value, sub, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide truncate">{label}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
            {sub && <p className="text-xs text-slate-400 mt-1 truncate">{sub}</p>}
          </div>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${COLOR_MAP[color] || COLOR_MAP.sky}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PlanBar({ label, value, total, color }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-slate-600">{label}</span>
        <span className="font-semibold text-slate-900">{value} <span className="text-slate-400 font-normal">({pct}%)</span></span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}
